import { toBig } from "@tradingApp/components/Trading/helpers/big";
import type { BigSource } from "big.js";

interface TradingCalculateLiquidationPriceParams {
  maxLeverage?: BigSource;
  orderDirection: "long" | "short";
  price: BigSource;
  orderSize: BigSource;
  availableToTrade: BigSource;
}

/**
 * Calculates the liquidation price for a given order.
 *
 * @url https://hyperliquid.gitbook.io/hyperliquid-docs/trading/liquidations
 */
export function tradingLiquidationPriceCalc(
  params: TradingCalculateLiquidationPriceParams,
): string {
  const {
    maxLeverage = 1,
    orderDirection,
    price,
    orderSize,
    availableToTrade,
  } = params;

  const priceBig = toBig(price);
  const orderSizeBig = toBig(orderSize);
  const leverageBig = toBig(maxLeverage);
  const avail = toBig(availableToTrade);
  const orderVal = priceBig.times(orderSizeBig);
  const side = orderDirection === "long" ? toBig(1) : toBig(-1);

  if (orderSizeBig.eq(0) || leverageBig.eq(0)) {
    return "0";
  }

  const maintenanceLeverage = leverageBig.times(2);
  const adjustmentFactor = toBig(1).minus(side.div(maintenanceLeverage));

  if (adjustmentFactor.eq(0)) {
    return "0";
  }

  const marginAvailable = avail.minus(orderVal.div(maintenanceLeverage));

  const liquidationDiff = side
    .times(marginAvailable)
    .div(orderSizeBig)
    .div(adjustmentFactor);

  const liquidationPrice = priceBig.minus(liquidationDiff);

  if (liquidationPrice.lte(0) || liquidationPrice.gt("1e15")) {
    return "0";
  }

  return liquidationPrice.toString();
}
